import { Outlet, type RouteObject } from "react-router-dom";

import { App } from "../App";
import { BreakerSelectivityPage } from "../pages/BreakerSelectivityPage";
import { BusbarPage } from "../pages/BusbarPage";
import { CableSizingPage } from "../pages/CableSizingPage";
import { CableTrayPage } from "../pages/CableTrayPage";
import { SurgeProtectionPage } from "../pages/SurgeProtectionPage";
import { ChangePasswordPage } from "../pages/ChangePasswordPage";
import { EarthingPage } from "../pages/EarthingPage";
import { EarthFaultLoopPage } from "../pages/EarthFaultLoopPage";
import { FaultStudyPage } from "../pages/FaultStudyPage";
import { GeneratorSizingPage } from "../pages/GeneratorSizingPage";
import { UPSSizingPage } from "../pages/UPSSizingPage";
import { PVSizingPage } from "../pages/PVSizingPage";
import { StarterCoordinationPage } from "../pages/StarterCoordinationPage";
import { LoadStudyPage } from "../pages/LoadStudyPage";
import { PowerQualityPage } from "../pages/PowerQualityPage";
import { LoginPage } from "../pages/LoginPage";
import { ProjectsPage } from "../pages/ProjectsPage";
import { ReportsPage } from "../pages/ReportsPage";
import { RunReportPage } from "../pages/RunReportPage";
import { SldPage } from "../pages/SldPage";
import { TransformerSizingPage } from "../pages/TransformerSizingPage";
import { UsersPage } from "../pages/UsersPage";
import { AppShell } from "./AppShell";
import { AuthProvider } from "./AuthProvider";
import { ProjectProvider } from "./ProjectProvider";
import {
  CHANGE_PASSWORD_PATH,
  LOGIN_PATH,
  PROJECTS_PATH,
  RequireAuth,
  SignedOutOnly,
  USERS_PATH,
} from "./routeGuards";

// The route table, kept apart from createBrowserRouter so tests can run it in a memory router.
//
// Every route sits under the AuthProvider, and the ProjectProvider sits inside it so the
// chosen project follows the session. Only the sign-in page is open; the shell and with it
// every module page is behind RequireAuth. A new page added under the shell is guarded
// without any further step.
export const routes: RouteObject[] = [
  {
    element: (
      <AuthProvider>
        <ProjectProvider>
          <Outlet />
        </ProjectProvider>
      </AuthProvider>
    ),
    children: [
      {
        path: LOGIN_PATH,
        element: (
          <SignedOutOnly>
            <LoginPage />
          </SignedOutOnly>
        ),
      },
      {
        path: CHANGE_PASSWORD_PATH,
        element: (
          <RequireAuth allowPasswordChangePending>
            <ChangePasswordPage />
          </RequireAuth>
        ),
      },
      {
        // Layout route: the shell renders navigation and footer around every page via <Outlet />.
        path: "/",
        element: (
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <App /> },
          { path: "load-demand", element: <LoadStudyPage /> },
          { path: "transformer-sizing", element: <TransformerSizingPage /> },
          { path: "generator-sizing", element: <GeneratorSizingPage /> },
          { path: "ups-sizing", element: <UPSSizingPage /> },
          { path: "pv-sizing", element: <PVSizingPage /> },
          { path: "starter-coordination", element: <StarterCoordinationPage /> },
          { path: "breaker-selectivity", element: <BreakerSelectivityPage /> },
          { path: "earth-fault-loop", element: <EarthFaultLoopPage /> },
          { path: "busbar", element: <BusbarPage /> },
          { path: "fault-study", element: <FaultStudyPage /> },
          { path: "cable-sizing", element: <CableSizingPage /> },
          // EOS-12 (A34): the cable tray and routing study of one tray route.
          { path: "cable-tray", element: <CableTrayPage /> },
          // EOS-10 (A35): the surge protection study of one SPD location.
          { path: "surge-protection", element: <SurgeProtectionPage /> },
          // EOS-08a (A36): the earthing arrangement of one bus.
          { path: "earthing", element: <EarthingPage /> },
          // EOS-11 (A32): one route with two modes behind `?mode=`, the power factor
          // correction (11a, the default) and the harmonic assessment (11b).
          { path: "power-quality", element: <PowerQualityPage /> },
          // Every signed-in member may read the project configuration; the server decides
          // who may write, and the page hides the actions a role cannot use.
          { path: PROJECTS_PATH.slice(1), element: <ProjectsPage /> },
          // The project revision's single-line diagram: EOS-01's second page (A26 (i)).
          { path: "sld", element: <SldPage /> },
          // EOS-13 (A31): the reports landing page (the module's route, every member may read
          // it, D5) and the printable report of one stored run - by run id, or the study's
          // approved-else-latest run by key.
          { path: "reports", element: <ReportsPage /> },
          { path: "reports/runs/:runId", element: <RunReportPage /> },
          { path: "reports/by-key", element: <RunReportPage /> },
          // Owner only: the page says so to anybody else, and the server refuses them (403).
          { path: USERS_PATH.slice(1), element: <UsersPage /> },
        ],
      },
    ],
  },
];
